
import Vue from 'vue';

import * as RootStore from '@/store/search/';
import * as CorpusStore from '@/store/search/corpus';
import * as UIStore from '@/store/search/ui';
import * as InterfaceStore from '@/store/search/form/interface';
import * as PatternStore from '@/store/search/form/patterns';
import * as GlossStore from '@/store/search/form/glossStore';
import * as ConceptStore from '@/store/search/form/conceptStore';
import * as GapStore from '@/store/search/form/gap';
import * as HistoryStore from '@/store/search/history';

import Annotation from '@/pages/search/form/Annotation.vue';
import SearchAdvanced from '@/pages/search/form/SearchAdvanced.vue';
import SearchExpert from '@/pages/search/form/SearchExpert.vue';
import ConceptSearch from '@/pages/search/form/concept/ConceptSearch.vue';
import GlossSearch from '@/pages/search/form/concept/GlossSearch.vue';
import ParallelSourceAndTargets from '@/pages/search/form/ParallelSourceAndTargets.vue';
import Within from '@/pages/search/form/Within.vue';
import uid from '@/mixins/uid';

import { blacklabPaths } from '@/api';
import * as AppTypes from '@/types/apptypes';
import { getAnnotationSubset } from '@/utils';

import { Option } from '@/components/SelectPicker.vue';

function isVue(v: any): v is Vue { return v instanceof Vue; }
function isJQuery(v: any): v is JQuery { return typeof v !== 'boolean' && v && v.jquery; }

import ParallelFields from './parallel/ParallelFields';
import { corpusCustomizations } from '@/utils/customization';

export default ParallelFields.extend({
	components: {
		ParallelSourceAndTargets,
		Annotation,
		SearchAdvanced,
		SearchExpert,
		ConceptSearch,
		GlossSearch,
		Within,
	},
	props: {
		errorNoParallelSourceVersion: {default: false, type: Boolean},
	},
	data: () => ({
		uid: uid(),
		parseQueryError: null as string|null,
		importQueryError: null as string|null,

		subscriptions: [] as Array<() => void>
	}),
	computed: {
		activePattern: {
			get(): string { return InterfaceStore.getState().patternMode; },
			set: InterfaceStore.actions.patternMode,
		},
		useTabs(): boolean {
			return this.tabs.length > 1;
		},
		tabs(): Array<{label?: string, entries: AppTypes.NormalizedAnnotation[]}> {
			const result = getAnnotationSubset(
				UIStore.getState().search.extended.searchAnnotationIds,
				CorpusStore.get.annotationGroups(),
				CorpusStore.get.allAnnotationsMap(),
				'Search',
				this,
				CorpusStore.get.textDirection()
			);
			if (this.isParallelCorpus) {
				// Make sure we have the correct field, so autosuggest works properly
				const field = PatternStore.getState().shared.source;
				if (field) {
					const fieldId = CorpusStore.get.allAnnotatedFieldsMap()[field]?.id;
					if (fieldId) {
						result.forEach(tab => {
							tab.entries = tab.entries.map(e => ({
								...e,
								annotatedFieldId: field
							}));
						});
					}
				}
			}
			return result;
		},
		allAnnotations(): AppTypes.NormalizedAnnotation[] {
			return this.tabs.flatMap(tab => tab.entries);
		},
		simpleSearchAnnotation(): AppTypes.NormalizedAnnotation {
			const field = (this.isParallelCorpus ? PatternStore.getState().shared.source : undefined)
				?? CorpusStore.get.mainAnnotatedField();
			const id = UIStore.getState().search.simple.searchAnnotationId;
			console.log(field, id);
			return CorpusStore.get.allAnnotatedFieldsMap()[field]?.annotations[id]
				|| CorpusStore.get.firstMainAnnotation();
		},
		simpleSearchAnnotationAutoCompleteUrl(): string { return blacklabPaths.autocompleteAnnotation(INDEX_ID, this.simpleSearchAnnotation.annotatedFieldId, this.simpleSearchAnnotation.id); },
		textDirection: CorpusStore.get.textDirection,
		withinOptions(): Option[] {
			const {enabled, elements} = UIStore.getState().search.shared.within;
			return enabled ? elements.filter(element => corpusCustomizations.search.within.includeSpan(element.value)) : [];
		},
		within(): string|null {
			return PatternStore.getState().shared.within;
		},
		splitBatchEnabled(): boolean {
			return UIStore.getState().search.extended.splitBatch.enabled &&
				!this.isParallelCorpus; // hide for parallel
		},
		splitBatch: {
			get(): boolean { return PatternStore.getState().extended.splitBatch; },
			set: PatternStore.actions.extended.splitBatch
		},
		simple: {
			get(): AppTypes.AnnotationValue { return PatternStore.getState().simple.annotationValue; },
			set: PatternStore.actions.simple.annotation,
		},
		advancedEnabled(): boolean { return UIStore.getState().search.advanced.enabled; },
		glossEnabled(): boolean { return GlossStore.get.settings() != null; },
		conceptEnabled(): boolean { return ConceptStore.get.settings() != null; },
		advanced: {
			get(): string|null { return PatternStore.getState().advanced.query; },
			set: PatternStore.actions.advanced.query,
		},
		concept: {
			get(): string|null { return PatternStore.getState().concept; },
			set: PatternStore.actions.concept,
		},
		glosses: {
			get(): string|null { return PatternStore.getState().glosses; },
			set: PatternStore.actions.glosses,
		},
		gapValue: {
			get: GapStore.get.gapValue,
			set: GapStore.actions.gapValue
		},

		customAnnotations() {
			return UIStore.getState().search.shared.customAnnotations;
		}
	},
	methods: {
		getTabId(name?: string) {
			return name?.replace(/[^\w]/g, '_') + '_annotations';
		},
		async parseQuery() {
			const expertQueries = [PatternStore.getState().expert.query, ...PatternStore.getState().expert.targetQueries];

			const queryBuilders = $('.querybuilder').map((_, el) => $(el).data('builder')).get();
			let success = true;
			for (let i = 0; i < queryBuilders.length; i++) {
				const builder = queryBuilders[i];
				if (builder) {
					if (!(await builder.parse(expertQueries[i]))) {
						success = false;
						break;
					}
				}
			}
			if (success) {
				InterfaceStore.actions.patternMode('advanced');
				this.parseQueryError = null;
				return;
			}
			this.parseQueryError = 'The querybuilder could not parse your query.';
		},
		importQuery(event: Event) {
			const el = (event.target as HTMLInputElement);
			if (!el.files || el.files.length !== 1) {
				return;
			}

			const file = el.files[0];
			HistoryStore.get.fromFile(file)
			.then(r => {
				RootStore.actions.replace(r.entry);
				this.importQueryError = null;
			})
			.catch(e => this.importQueryError = e.message)
			.finally(() => el.value = '')
		},
		importGapFile(event: Event) {
			const self = this;
			const el = (event.target as HTMLInputElement);
			if (!el.files || el.files.length !== 1) {
				self.gapValue = null;
				return;
			}
			GapStore.actions.gapValueFile(el.files[0]);
			el.value = '';
		},
		insertTabInText(event: Event) {
			const el = event.target as HTMLTextAreaElement;
			let text = el.value;

			const originalSelectionStart = el.selectionStart;
			const originalSelectionEnd = el.selectionEnd;
			const textStart = text.slice(0, originalSelectionStart);
			const textEnd =  text.slice(originalSelectionEnd);

			el.value = `${textStart}\t${textEnd}`;
			el.selectionEnd = el.selectionStart = originalSelectionStart + 1;
		},

		copyConceptQuery() {
			PatternStore.actions.expert.query(this.concept);
			InterfaceStore.actions.patternMode('expert');
		},
		copyGlossQuery() {
			PatternStore.actions.expert.query(this.glosses);
			InterfaceStore.actions.patternMode('expert');
		},
		setupCustomAnnotation(div: HTMLElement, plugin: NonNullable<UIStore.ModuleRootState['search']['shared']['customAnnotations'][string]>) {
			const annotId = div.getAttribute('data-custom-annotation-root')!;
			const isSimpleAnnotation = div.hasAttribute('data-is-simple');

			const config = CorpusStore.get.allAnnotationsMap()[annotId];
			const value = isSimpleAnnotation ? PatternStore.getState().simple.annotationValue : PatternStore.getState().extended.annotationValues[annotId];

			const {render, update} = plugin;
			const ui = render(config, value, Vue);

			if (typeof ui === 'string') div.innerHTML = ui;
			else if (ui instanceof HTMLElement) div.appendChild(ui);
			else if (isJQuery(ui)) ui.appendTo(div);
			else if (isVue(ui)) ui.$mount(div);

			if (!isVue(ui) && update != null) {
				// setup watcher so custom component is notified of changes to its value by external processes (global form reset, history state restore, etc.)
				RootStore.store.watch(state => value, (cur, prev) => update(cur, prev, div), {deep: true});
			}
		},
	},
	watch: {
		customAnnotations: {
			handler() {
				// custom annotation widget setup.
				// listen for changes, so any late registration is also picked up
				Vue.nextTick(() => {
					// intermediate function, check if div is not already initialized, and should actually become the custom component.
					const setup = (key: string, div: Element|Vue) => {
						if (!(div instanceof HTMLElement) || !div.hasAttribute('data-custom-annotation-root') || div.children.length) return;
						const annotId = div.getAttribute('data-custom-annotation-root')!;
						this.setupCustomAnnotation(div, this.customAnnotations[annotId]!)
					}

					// by now our dom should have updates, and the extension point (div) should be present
					// scan to find it.
					Object.entries(this.$refs).forEach(([refId, ref]) => {
						if (Array.isArray(ref)) ref.forEach(r => setup(refId, r));
						else if (ref instanceof HTMLElement) setup(refId, ref);
					});
				})
			},
			immediate: true,
			deep: true
		}
	},
	mounted() {
		if (this.$refs.reset) {
			const eventId = `${PatternStore.namespace}/reset`;

			this.subscriptions.push(RootStore.store.subscribe((mutation, state) => {
				if (this.$refs.reset && mutation.type === eventId) {
					(this.$refs.reset as any).reset();
				}
			}));
		}
	}
})
